import {
	isNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { ClientValidationErrorModel } from "client/Api/common";
import { HttpRequestError } from "client/Api/httpClient";
import { InputValidationResultKind, InvalidInputValidationResult } from "./input";
import { ModelPath } from "./path";
import { ModelPathMap } from "./state";

export function tryParseServerErrors(
	error: unknown,
	pathRedirects?: readonly PathRedirect[]
): TryResult<ReadonlyMap<ModelPath, InvalidInputValidationResult>> {
	if (!(error instanceof HttpRequestError && error.statusCode === 400)) {
		return { wasSuccessful: false };
	}

	try {
		let errors = error.readJson<ClientValidationErrorModel[] | IUserFriendlyExceptionMessage>();
		if (!Array.isArray(errors)) {
			errors = [
				{
					propertyName: "",
					errorMessage: errors.message
				}
			];
		}

		return {
			wasSuccessful: true,
			result: convertServerErrors(errors, pathRedirects)
		};
	} catch (e) {
		console.error(e);
		return { wasSuccessful: false };
	}
}

export interface ReplacementPattern {
	tryReplace(path: string, replacement: string): TryResult<string>;
}

export type PathRedirect = {
	pattern: string | ReplacementPattern;
	replacement: string;
};

export function convertServerErrors(
	errors: readonly ClientValidationErrorModel[],
	pathRedirects?: readonly PathRedirect[]
): ReadonlyMap<ModelPath, InvalidInputValidationResult> {
	const result = new ModelPathMap<InvalidInputValidationResult>();

	for (const error of errors) {
		result.set(
			convertPath(error.propertyName, pathRedirects),
			{
				kind: InputValidationResultKind.Invalid,
				errorMessage: error.errorMessage
			}
		);
	}

	return result;
}

function convertPath(
	serverPath: string,
	pathRedirects?: readonly PathRedirect[]
): ModelPath {
	const path: string = isNotNil(pathRedirects)
		? updatePathWithRedirects(serverPath, pathRedirects)
		: serverPath;

	return parseModelPath(path, ModelPath.root);
};

const collectionIndexRegex: RegExp = /^\[(\d+)\](.*)$/;
const propertyRegex: RegExp = /^(\.?)([^\.\[]*)(.*)$/;

function parseModelPath(serverModelPath: string, parentPath: ModelPath): ModelPath {
	if (!serverModelPath) {
		return parentPath;
	}

	const collectionMatch: RegExpExecArray | null = collectionIndexRegex.exec(serverModelPath);

	if (collectionMatch) {
		const index: number = parseInt(collectionMatch[1], 10);
		const restOfThePath: string = collectionMatch[2];

		return parseModelPath(
			restOfThePath,
			parentPath.forIndex(index)
		);
	}

	const propertyMatch: RegExpExecArray | null = propertyRegex.exec(serverModelPath);

	if (propertyMatch) {
		const hasDot: boolean = isNotNilOrEmpty(propertyMatch[1]);
		const propertyName: string = deCapitalizeFirstLetter(propertyMatch[2]);
		const restOfThePath: string = propertyMatch[3];

		if (!hasDot && !parentPath.equals(ModelPath.root)) {
			throw new Error("Encountered a property segment which does not start with the dot.");
		}

		return parseModelPath(
			restOfThePath,
			parentPath.for(propertyName)
		);
	}

	throw new Error(`Unable to parse server path '${serverModelPath}'.`);
}

function deCapitalizeFirstLetter (value: string): string {
	if (!value) {
		return value;
	}
	return value.charAt(0).toLowerCase() + value.slice(1);
};

function updatePathWithRedirects(serverPath: string, redirects: readonly PathRedirect[]): string {
	for (const redirect of redirects) {
		const redirectResult: TryResult<string> = tryApplyRedirect(serverPath, redirect);

		if (redirectResult.wasSuccessful) {
			return redirectResult.result;
		}
	}

	return serverPath;
}

function tryApplyRedirect(serverPath: string, redirect: PathRedirect): TryResult<string> {
	if (typeof redirect.pattern !== "string") {
		return redirect.pattern
			.tryReplace(serverPath, redirect.replacement);
	}

	if (serverPath === redirect.pattern) {
		return {
			wasSuccessful: true,
			result: redirect.replacement
		};
	}

	return {
		wasSuccessful: false
	};
}
